import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, RefreshCw, Download, Edit, Trash2, Plus,
  Clock, Loader2, Share2, UserCheck
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

import { Switch } from "../../components/ui/switch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EventsTab = ({ token, isSuperAdmin, requestDelete, canEdit = true }) => {
  const [rsvps, setRsvps] = useState([]);
  const [rsvpsLoading, setRsvpsLoading] = useState(false);
  const [rsvpSearch, setRsvpSearch] = useState("");
  const [rsvpAttendanceFilter, setRsvpAttendanceFilter] = useState("all");
  const [rsvpStatusFilter, setRsvpStatusFilter] = useState("all");
  const [showAddRsvp, setShowAddRsvp] = useState(false);
  const [showEditRsvp, setShowEditRsvp] = useState(false);
  const [editingRsvp, setEditingRsvp] = useState(null);
  const [rsvpForm, setRsvpForm] = useState({ first_name: "", last_name: "", email: "", job_title: "", company: "", mobile: "" });
  const [savingRsvp, setSavingRsvp] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [togglingRsvp, setTogglingRsvp] = useState(false);

  const fetchRsvps = useCallback(async () => {
    setRsvpsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/rsvps`, { headers: { Authorization: `Bearer ${token}` } });
      setRsvps(response.data);
    } catch { toast.error("Failed to fetch RSVPs"); }
    finally { setRsvpsLoading(false); }
  }, [token]);

  useEffect(() => { fetchRsvps(); }, [fetchRsvps]);

  // Fetch RSVP open/closed status
  useEffect(() => {
    axios.get(`${API}/settings/rsvp-status`)
      .then(res => setRsvpOpen(res.data.rsvp_open))
      .catch(() => {});
  }, []);

  const handleToggleRsvp = async () => {
    setTogglingRsvp(true);
    try {
      const res = await axios.post(`${API}/admin/settings/toggle-rsvp`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRsvpOpen(res.data.rsvp_open);
      toast.success(res.data.rsvp_open ? "RSVP is now open" : "RSVP is now closed");
    } catch { toast.error("Failed to toggle RSVP status"); }
    finally { setTogglingRsvp(false); }
  };

  // Auto-refresh every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => { fetchRsvps(); }, 120000);
    return () => clearInterval(interval);
  }, [fetchRsvps]);

  const filteredRsvps = useMemo(() => {
    return rsvps.filter(r => {
      const q = rsvpSearch.toLowerCase();
      const matchesSearch = !q || [r.first_name, r.last_name, r.email, r.company, r.job_title, r.mobile].some(v => (v || "").toLowerCase().includes(q));
      const matchesStatus = rsvpStatusFilter === "all" || r.status === rsvpStatusFilter;
      const matchesAttendance = rsvpAttendanceFilter === "all" || (rsvpAttendanceFilter === "attended" && r.attended) || (rsvpAttendanceFilter === "not_attended" && !r.attended);
      return matchesSearch && matchesStatus && matchesAttendance;
    });
  }, [rsvps, rsvpSearch, rsvpStatusFilter, rsvpAttendanceFilter]);

  const handleExportRsvps = async () => {
    try {
      const response = await axios.get(`${API}/admin/rsvps/export`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'rsvps_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error("Export failed"); }
  };

  const handleToggleAttendance = async (rsvpId) => {
    try {
      const res = await axios.patch(`${API}/admin/rsvps/${rsvpId}/attendance`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRsvps(prev => prev.map(r => r.id === rsvpId ? { ...r, attended: res.data.attended } : r));
    } catch { toast.error("Failed to update attendance"); }
  };

  const handleChangeRsvpStatus = async (rsvpId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/rsvps/${rsvpId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setRsvps(prev => prev.map(r => r.id === rsvpId ? { ...r, status: newStatus } : r));
      toast.success(`Status updated to ${newStatus}`);
    } catch { toast.error("Failed to update status"); }
  };

  const handleSendConfirmation = async (rsvpId) => {
    setSendingEmail(rsvpId);
    try {
      const res = await axios.post(`${API}/admin/rsvps/${rsvpId}/send-confirmation`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRsvps(prev => prev.map(r => r.id === rsvpId ? { ...r, email_sent: true } : r));
      toast.success(res.data.message || "Email sent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send email"); }
    finally { setSendingEmail(null); }
  };

  const handleSendReminder = async (rsvpId) => {
    setSendingEmail(rsvpId);
    try {
      const res = await axios.post(`${API}/admin/rsvps/${rsvpId}/send-reminder`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRsvps(prev => prev.map(r => r.id === rsvpId ? { ...r, reminder_sent: true } : r));
      toast.success(res.data.message || "Reminder sent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send reminder"); }
    finally { setSendingEmail(null); }
  };

  const handleAddRsvp = async (e) => {
    e.preventDefault();
    setSavingRsvp(true);
    try {
      const res = await axios.post(`${API}/admin/rsvps`, rsvpForm, { headers: { Authorization: `Bearer ${token}` } });
      setRsvps(prev => [res.data, ...prev]);
      setShowAddRsvp(false);
      toast.success("Guest added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add guest"); }
    finally { setSavingRsvp(false); }
  };

  const openEditRsvp = (rsvp) => {
    setEditingRsvp(rsvp);
    setRsvpForm({ first_name: rsvp.first_name, last_name: rsvp.last_name, email: rsvp.email || "", job_title: rsvp.job_title || "", company: rsvp.company || "", mobile: rsvp.mobile || "" });
    setShowEditRsvp(true);
  };

  const handleEditRsvp = async (e) => {
    e.preventDefault();
    if (!editingRsvp) return;
    setSavingRsvp(true);
    try {
      const res = await axios.put(`${API}/admin/rsvps/${editingRsvp.id}`, rsvpForm, { headers: { Authorization: `Bearer ${token}` } });
      setRsvps(prev => prev.map(r => r.id === editingRsvp.id ? res.data : r));
      setShowEditRsvp(false);
      toast.success("Guest updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update guest"); }
    finally { setSavingRsvp(false); }
  };

  const handleDeleteRsvp = (rsvpId) => {
    const rsvp = rsvps.find(r => r.id === rsvpId);
    requestDelete("rsvp", rsvpId, `${rsvp?.first_name} ${rsvp?.last_name}`, async () => {
      try {
        await axios.delete(`${API}/admin/rsvps/${rsvpId}`, { headers: { Authorization: `Bearer ${token}` } });
        setRsvps(prev => prev.filter(r => r.id !== rsvpId));
        toast.success("Guest removed");
      } catch { toast.error("Failed to delete guest"); }
    });
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white font-mono" data-testid="events-title">Events &amp; RSVPs</h1>
          <p className="text-[#ebebeb]/60 mt-1 font-mono text-sm">4th Anniversary Party — 17 Apr 2026</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isSuperAdmin && (
            <div className="flex items-center gap-2 py-2 px-4 rounded-full border border-white/10" data-testid="rsvp-toggle-container">
              <span className={`font-mono text-sm ${rsvpOpen ? 'text-green-400' : 'text-red-400'}`}>
                RSVP {rsvpOpen ? 'Open' : 'Closed'}
              </span>
              <Switch
                checked={rsvpOpen}
                onCheckedChange={handleToggleRsvp}
                disabled={togglingRsvp}
                data-testid="rsvp-toggle-switch"
                className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-500/60"
              />
            </div>
          )}
          <button onClick={fetchRsvps} className="flex items-center gap-2 py-2 px-4 rounded-full border border-white/10 text-[#ebebeb]/60 hover:text-white hover:bg-white/5 transition-colors font-mono text-sm" data-testid="refresh-rsvps-btn">
            <RefreshCw className={`w-4 h-4 ${rsvpsLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={handleExportRsvps} className="flex items-center gap-2 py-2 px-4 rounded-full border border-white/10 text-[#ebebeb]/60 hover:text-white hover:bg-white/5 transition-colors font-mono text-sm" data-testid="export-rsvps-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => { setRsvpForm({ first_name: "", last_name: "", email: "", job_title: "", company: "", mobile: "" }); setShowAddRsvp(true); }} className={`ingenium-button text-sm ${canEdit ? "" : "hidden"}`} data-testid="add-rsvp-btn">
            <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Add Guest</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {[
          { label: "Total Guests", value: rsvps.length, color: "text-white", testid: "rsvp-total-count" },
          { label: "Invited", value: rsvps.filter(r => r.status === "invited").length, color: "text-blue-400" },
          { label: "Confirmed", value: rsvps.filter(r => r.status === "confirmed").length, color: "text-yellow-400" },
          { label: "Approved", value: rsvps.filter(r => r.status === "approved").length, color: "text-green-400" },
          { label: "Checked In", value: rsvps.filter(r => r.attended).length, color: "text-[#ff3737]", testid: "rsvp-attended-count" },
        ].map(s => (
          <Card key={s.label} className="bg-[#1e2124] border-white/5">
            <CardContent className="p-4">
              <p className="text-[#ebebeb]/40 text-xs font-mono mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`} data-testid={s.testid}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ebebeb]/30" />
          <Input placeholder="Search by name, email, company, or phone..." value={rsvpSearch} onChange={(e) => setRsvpSearch(e.target.value)} className="pl-10 bg-[#1e2124] border-white/5 text-white font-mono text-sm" data-testid="rsvp-search" />
        </div>
        <Select value={rsvpStatusFilter} onValueChange={setRsvpStatusFilter}>
          <SelectTrigger className="w-[160px] bg-[#1e2124] border-white/5 text-[#ebebeb]/80 font-mono text-sm" data-testid="rsvp-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent className="bg-[#1e2124] border-white/10">
            <SelectItem value="all" className="text-white font-mono">All Statuses</SelectItem>
            <SelectItem value="invited" className="text-white font-mono">Invited</SelectItem>
            <SelectItem value="confirmed" className="text-white font-mono">Confirmed</SelectItem>
            <SelectItem value="approved" className="text-white font-mono">Approved</SelectItem>
            <SelectItem value="declined" className="text-white font-mono">Declined</SelectItem>
          </SelectContent>
        </Select>
        <Select value={rsvpAttendanceFilter} onValueChange={setRsvpAttendanceFilter}>
          <SelectTrigger className="w-[160px] bg-[#1e2124] border-white/5 text-[#ebebeb]/80 font-mono text-sm" data-testid="rsvp-attendance-filter"><SelectValue placeholder="All Guests" /></SelectTrigger>
          <SelectContent className="bg-[#1e2124] border-white/10">
            <SelectItem value="all" className="text-white font-mono">All Guests</SelectItem>
            <SelectItem value="attended" className="text-white font-mono">Checked In</SelectItem>
            <SelectItem value="not_attended" className="text-white font-mono">Not Checked In</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* RSVP Table */}
      {rsvpsLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#ff3737]" /></div>
      ) : (
        <Card className="bg-[#1e2124] border-white/5">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs w-12">CHECK-IN</TableHead>
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs">NAME</TableHead>
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs">EMAIL</TableHead>
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs">STATUS</TableHead>
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs hidden lg:table-cell">COMPANY</TableHead>
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs hidden xl:table-cell">MOBILE</TableHead>
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs hidden xl:table-cell">LOCATION</TableHead>
                  <TableHead className="text-[#ebebeb]/40 font-mono text-xs text-right">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRsvps.length > 0 ? filteredRsvps.map((rsvp) => (
                  <TableRow key={rsvp.id} className={`border-white/5 hover:bg-white/[0.02] ${rsvp.status === "declined" ? "opacity-40" : ""}`}>
                    <TableCell>
                      <button onClick={() => handleToggleAttendance(rsvp.id)} disabled={!canEdit}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${rsvp.attended ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-white/5 text-[#ebebeb]/20 hover:bg-white/10 hover:text-[#ebebeb]/40'} ${canEdit ? "" : "cursor-not-allowed opacity-60"}`}
                        data-testid={`rsvp-checkin-${rsvp.id}`} title={canEdit ? (rsvp.attended ? "Mark as not attended" : "Check in guest") : "Read-only"}>
                        <UserCheck className="w-4 h-4" />
                      </button>
                    </TableCell>
                    <TableCell><div><p className="text-white text-sm font-medium">{rsvp.first_name} {rsvp.last_name}</p><p className="text-[#ebebeb]/30 text-xs">{rsvp.job_title || ""}</p></div></TableCell>
                    <TableCell className="text-[#ebebeb]/60 text-sm">{rsvp.email || <span className="text-[#ebebeb]/20 italic">No email</span>}</TableCell>
                    <TableCell>
                      <Select value={rsvp.status || "invited"} onValueChange={(val) => handleChangeRsvpStatus(rsvp.id, val)} disabled={!canEdit}>
                        <SelectTrigger className={`w-[120px] h-7 text-xs font-mono border-0 ${rsvp.status === "approved" ? "bg-green-500/15 text-green-400" : rsvp.status === "confirmed" ? "bg-yellow-500/15 text-yellow-400" : rsvp.status === "declined" ? "bg-red-500/15 text-red-400" : "bg-blue-500/15 text-blue-400"}`} data-testid={`rsvp-status-${rsvp.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1e2124] border-white/10">
                          <SelectItem value="invited" className="text-blue-400 font-mono text-xs">Invited</SelectItem>
                          <SelectItem value="confirmed" className="text-yellow-400 font-mono text-xs">Confirmed</SelectItem>
                          <SelectItem value="approved" className="text-green-400 font-mono text-xs">Approved</SelectItem>
                          <SelectItem value="declined" className="text-red-400 font-mono text-xs">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-[#ebebeb]/60 text-sm hidden lg:table-cell">{rsvp.company || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/60 text-sm hidden xl:table-cell">{rsvp.mobile || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/60 text-sm hidden xl:table-cell">{rsvp.location || "-"}</TableCell>
                    <TableCell className="text-right">
                      {canEdit ? (
                        <div className="flex items-center justify-end gap-1">
                          {rsvp.status === "approved" && rsvp.email && (
                            <button onClick={() => handleSendConfirmation(rsvp.id)} disabled={sendingEmail === rsvp.id}
                              className={`p-2 rounded-lg transition-colors ${rsvp.email_sent ? 'text-green-400/60 hover:text-green-400 hover:bg-green-500/10' : 'text-[#ff3737]/60 hover:text-[#ff3737] hover:bg-[#ff3737]/10'}`}
                              data-testid={`rsvp-send-email-${rsvp.id}`} title={rsvp.email_sent ? "Resend confirmation email" : "Send confirmation email"}>
                              {sendingEmail === rsvp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                            </button>
                          )}
                          {rsvp.status === "invited" && rsvp.email && (
                            <button onClick={() => handleSendReminder(rsvp.id)} disabled={sendingEmail === rsvp.id}
                              className={`p-2 rounded-lg transition-colors ${rsvp.reminder_sent ? 'text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10' : 'text-yellow-500/60 hover:text-yellow-500 hover:bg-yellow-500/10'}`}
                              data-testid={`rsvp-send-reminder-${rsvp.id}`} title={rsvp.reminder_sent ? "Resend reminder email" : "Send reminder email"}>
                              {sendingEmail === rsvp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                            </button>
                          )}
                          <button onClick={() => openEditRsvp(rsvp)} className="p-2 text-[#ebebeb]/40 hover:text-white rounded-lg hover:bg-white/5" data-testid={`rsvp-edit-${rsvp.id}`}><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteRsvp(rsvp.id)} className="p-2 text-[#ebebeb]/40 hover:text-red-400 rounded-lg hover:bg-red-500/10" data-testid={`rsvp-delete-${rsvp.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <span className="text-[#ebebeb]/30 text-xs font-mono">Read-only</span>
                      )}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={8} className="text-center text-[#ebebeb]/30 py-12 font-mono">No guests found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add RSVP Modal */}
      <Dialog open={showAddRsvp} onOpenChange={setShowAddRsvp}>
        <DialogContent className="bg-[#191b1d] border-[#ff3737]/20 max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold text-white font-mono">Add Guest</DialogTitle><DialogDescription className="text-[#ebebeb]/60 font-mono">Manually add a guest to the RSVP list</DialogDescription></DialogHeader>
          <form onSubmit={handleAddRsvp} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-[#ebebeb]">First Name *</Label><Input placeholder="First name" value={rsvpForm.first_name} onChange={(e) => setRsvpForm(p => ({...p, first_name: e.target.value}))} className="form-input" data-testid="add-rsvp-first-name" required /></div>
              <div className="space-y-2"><Label className="text-[#ebebeb]">Last Name *</Label><Input placeholder="Last name" value={rsvpForm.last_name} onChange={(e) => setRsvpForm(p => ({...p, last_name: e.target.value}))} className="form-input" data-testid="add-rsvp-last-name" required /></div>
            </div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Email *</Label><Input type="email" placeholder="email@company.com" value={rsvpForm.email} onChange={(e) => setRsvpForm(p => ({...p, email: e.target.value}))} className="form-input" data-testid="add-rsvp-email" required /></div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Job Title</Label><Input placeholder="e.g. Media Director" value={rsvpForm.job_title} onChange={(e) => setRsvpForm(p => ({...p, job_title: e.target.value}))} className="form-input" data-testid="add-rsvp-job-title" /></div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Company</Label><Input placeholder="Company name" value={rsvpForm.company} onChange={(e) => setRsvpForm(p => ({...p, company: e.target.value}))} className="form-input" data-testid="add-rsvp-company" /></div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Mobile</Label><Input type="tel" placeholder="+852 XXXX XXXX" value={rsvpForm.mobile} onChange={(e) => setRsvpForm(p => ({...p, mobile: e.target.value}))} className="form-input" data-testid="add-rsvp-mobile" /></div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowAddRsvp(false)} className="flex-1 py-3 px-6 rounded-full border border-white/20 text-[#ebebeb] hover:bg-white/5 transition-colors font-mono">Cancel</button>
              <button type="submit" disabled={savingRsvp} className="ingenium-button flex-1 text-center" data-testid="save-new-rsvp-btn">
                <span className="flex items-center justify-center gap-2">{savingRsvp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}{savingRsvp ? "Adding..." : "Add Guest"}</span>
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit RSVP Modal */}
      <Dialog open={showEditRsvp} onOpenChange={setShowEditRsvp}>
        <DialogContent className="bg-[#191b1d] border-[#ff3737]/20 max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold text-white font-mono">Edit Guest</DialogTitle><DialogDescription className="text-[#ebebeb]/60 font-mono">Update guest details</DialogDescription></DialogHeader>
          <form onSubmit={handleEditRsvp} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-[#ebebeb]">First Name *</Label><Input placeholder="First name" value={rsvpForm.first_name} onChange={(e) => setRsvpForm(p => ({...p, first_name: e.target.value}))} className="form-input" data-testid="edit-rsvp-first-name" required /></div>
              <div className="space-y-2"><Label className="text-[#ebebeb]">Last Name *</Label><Input placeholder="Last name" value={rsvpForm.last_name} onChange={(e) => setRsvpForm(p => ({...p, last_name: e.target.value}))} className="form-input" data-testid="edit-rsvp-last-name" required /></div>
            </div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Email *</Label><Input type="email" placeholder="email@company.com" value={rsvpForm.email} onChange={(e) => setRsvpForm(p => ({...p, email: e.target.value}))} className="form-input" data-testid="edit-rsvp-email" required /></div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Job Title</Label><Input placeholder="e.g. Media Director" value={rsvpForm.job_title} onChange={(e) => setRsvpForm(p => ({...p, job_title: e.target.value}))} className="form-input" data-testid="edit-rsvp-job-title" /></div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Company</Label><Input placeholder="Company name" value={rsvpForm.company} onChange={(e) => setRsvpForm(p => ({...p, company: e.target.value}))} className="form-input" data-testid="edit-rsvp-company" /></div>
            <div className="space-y-2"><Label className="text-[#ebebeb]">Mobile</Label><Input type="tel" placeholder="+852 XXXX XXXX" value={rsvpForm.mobile} onChange={(e) => setRsvpForm(p => ({...p, mobile: e.target.value}))} className="form-input" data-testid="edit-rsvp-mobile" /></div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowEditRsvp(false)} className="flex-1 py-3 px-6 rounded-full border border-white/20 text-[#ebebeb] hover:bg-white/5 transition-colors font-mono">Cancel</button>
              <button type="submit" disabled={savingRsvp} className="ingenium-button flex-1 text-center" data-testid="save-edit-rsvp-btn">
                <span className="flex items-center justify-center gap-2">{savingRsvp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit className="w-4 h-4" />}{savingRsvp ? "Saving..." : "Save Changes"}</span>
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EventsTab;
